import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Loader, Stack, Tooltip } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { ContextModalProps } from "@mantine/modals";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { useGetTruckBrandsQuery, useGetTruckModelQuery, useSaveTruckModelMutation } from "@/store";
import { transformInOptions } from "@/utils";

import { Input } from "../../_Input";

type Schema = z.infer<typeof schema>;
const schema = z.object({
  id: z.number().nullable(),
  model: z.string().min(2, "Mínimo de 2 caracteres").max(100, "Máximo de 100 caracteres"),
  truckBrand: z.string().min(1, "Marca é obrigatório")
});

type CreateEditTruckModelModalProps = Omit<Partial<Schema>, "truckBrand">;
export function CreateEditTruckModelModal({ id, context, innerProps }: ContextModalProps<CreateEditTruckModelModalProps>) {
  const { id: innerId = null, model = "" } = innerProps;

  const [saveTruckModel] = useSaveTruckModelMutation();
  const { data: truckModel, isLoading: isLoadingTruckModel } = useGetTruckModelQuery({ id: innerId! }, { skip: !innerId });
  const { data: truckBrands, isLoading } = useGetTruckBrandsQuery(undefined, {
    selectFromResult: (state) => ({ ...state, data: transformInOptions(state.data) })
  });
  const [debouncedValue, setDebouncedValue] = useDebouncedState("", 300);

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty }
  } = useForm({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      id: innerId,
      model,
      truckBrand: truckModel ? String(truckModel?.truckBrand.id) : ""
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const body = Object.assign({}, { id: data.id, name: data.model, truckBrand: { id: Number(data.truckBrand) } });
    await saveTruckModel(body);

    handleDeny();
  };

  const handleDeny = () => {
    context.closeModal(id);
    reset();
  };

  const handleSearchModel = (value: string) => {
    setDebouncedValue(value);
  };

  useEffect(() => {
    if (truckModel) {
      reset((values) => ({
        ...values,
        truckBrand: String(truckModel.truckBrand.id)
      }));
      setDebouncedValue(truckModel?.truckBrand.name);
    }
  }, [truckModel]);

  return (
    <Stack>
      <Input.Select
        label="Marca"
        placeholder="Selecione a marca do caminhão"
        data={truckBrands}
        control={control}
        leftSection={(isLoading || isLoadingTruckModel) && <Loader size="sm" />}
        onSearchChange={(val) => handleSearchModel(val)}
        defaultSearchValue={debouncedValue || truckModel?.truckBrand.name}
        name="truckBrand"
        withAsterisk
      />
      <Input.Text label="Modelo" placeholder="Digite o modelo do caminhão" control={control} name="model" withAsterisk />

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Button variant="default" onClick={handleDeny} disabled={isSubmitting}>
          Cancelar
        </Button>

        <Tooltip label="Nenhuma alteração detectada!" color="red" disabled={!isDirty}>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} color={innerId ? "blue" : "green"} disabled={!isDirty}>
            {innerId ? "Salvar Alterações" : "Salvar"}
          </Button>
        </Tooltip>
      </div>
    </Stack>
  );
}
